import { api } from '@/lib/api'
import type { CallRecordingResponse } from '@tenfour/contracts'

export interface Conversation {
  id: string
  /** Linked customer (null for participant-only / Account threads). Lets
   *  customer-keyed views match a job to its customer's conversation. */
  customerId: string | null
  type: 'SMS' | 'VOICE'
  participantType?: 'CUSTOMER' | 'CONTRACTOR' | 'UNKNOWN'
  customerName: string | null
  customerPhone: string | null
  lastMessagePreview: string | null
  lastMessageAt: string | null
  unreadCount: number
  state: string
  assignedAgentId: string | null
  hasPendingAction: boolean
  hasOverdueInvoice: boolean
  /** Present in merged mode (`filter` param): sibling voice calls folded onto
   *  the SMS thread by phone. Standalone call rows carry callCount 1. */
  callCount?: number
  lastCallAt?: string | null
}

export interface ConversationListResponse {
  items: Conversation[]
  nextCursor: string | null
}

export interface ConversationMessage {
  id: string
  body: string
  direction: 'INBOUND' | 'OUTBOUND'
  senderType: string
  status: string
  fromNumber: string | null
  toNumber: string | null
  createdAt: string
}

/** A sibling voice call of an SMS thread. Each call is its own VOICE
 *  conversation row — fetch its transcript via `getConversation(call.id)`. */
export interface ConversationCall {
  id: string
  startedAt: string
  endedAt: string | null
  turnCount: number
}

export interface ConversationDetail {
  conversation: {
    id: string
    customerId: string | null
    type: 'SMS' | 'VOICE'
    participantType?: Conversation['participantType']
    customerName: string | null
    customerPhone: string | null
    state: string
    assignedAgentId: string | null
    assignedAgentName: string | null
    lastMessageAt: string | null
  }
  messages: ConversationMessage[]
  /** Empty for VOICE conversations; sibling calls for SMS threads. */
  calls?: ConversationCall[]
  nextCursor: string | null
}

export type ConversationFilter = 'all' | 'chats' | 'calls'

export async function listConversations(
  params?: {
    limit?: number
    cursor?: string
    type?: 'SMS' | 'VOICE'
    /** Merged chats+calls mode — `type` is ignored when set. */
    filter?: ConversationFilter
  },
): Promise<ConversationListResponse> {
  const q = new URLSearchParams()
  if (params?.limit) q.set('limit', String(params.limit))
  if (params?.cursor) q.set('cursor', params.cursor)
  if (params?.type) q.set('type', params.type)
  if (params?.filter) q.set('filter', params.filter)
  const qs = q.toString()
  return api(`/conversations${qs ? `?${qs}` : ''}`)
}

export async function getConversation(
  id: string,
  params?: { limit?: number; cursor?: string },
): Promise<ConversationDetail> {
  const q = new URLSearchParams()
  if (params?.limit) q.set('limit', String(params.limit))
  if (params?.cursor) q.set('cursor', params.cursor)
  const qs = q.toString()
  return api(`/conversations/${id}${qs ? `?${qs}` : ''}`)
}

/** Account-authorized metadata and a short-lived playback URL, when available. */
export async function getCallRecording(id: string, signal?: AbortSignal): Promise<CallRecordingResponse> {
  return api(`/conversations/${encodeURIComponent(id)}/recording`, { signal })
}

/**
 * Reply to a customer from the account portal. Hits the account-scoped
 * `POST /conversations/:id/send`, which inserts an `sms.reply` outbox event in
 * the same tx (the worker relays it to Twilio — the API never calls Twilio
 * directly). `body` must be 1–1500 chars (enforced by SendMessageDto).
 */
export interface NeedsYouReplyContext {
  itemKey: string
  sourceEventAt: string
  aiSuggestion?: string
}

export async function sendMessage(
  id: string,
  body: string,
  needsYou?: NeedsYouReplyContext,
  handoffRequestId?: string,
): Promise<{
  success: boolean
  messageId: string
  needsYouResolutionId?: string
  handoffResolutionId?: string
}> {
  return api(`/conversations/${id}/send`, {
    method: 'POST',
    body: JSON.stringify({ body, needsYou, handoffRequestId }),
  })
}

/**
 * The durable AI draft for this conversation's current inbound message (#538).
 * Reading never invokes the model — the worker's `reply_suggestion.generate`
 * outbox handler already wrote the row when the message arrived — so viewing
 * or refreshing Home can never accrue LLM spend. `pending` means a generation
 * is still queued or running; `suggestedAt` is when the draft was written.
 */
export interface SuggestedReply {
  suggestion: string | null
  suggestedAt: string | null
  revision: number | null
  pending: boolean
}

export async function getSuggestedReply(id: string): Promise<SuggestedReply> {
  return api(`/conversations/${id}/suggested-reply`)
}

/** Explicitly queue a new immutable draft revision for the current inbound. */
export async function regenerateSuggestedReply(
  id: string,
): Promise<{ queued: boolean; revision: number }> {
  return api(`/conversations/${id}/suggested-reply/regenerate`, { method: 'POST' })
}
